import { absoluteMediaUrl, resolveCanvasMediaUrl } from '@/lib/media';
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Copy, Download, Check, Palette, User, Mail, Phone, Globe, Building2, Briefcase, MapPin, Eye, ChevronDown, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_ICONS = {
  instagram: { color: '#E4405F', icon: 'https://cdn.simpleicons.org/instagram/E4405F' },
  facebook: { color: '#1877F2', icon: 'https://cdn.simpleicons.org/facebook/1877F2' },
  x: { color: '#000000', icon: 'https://cdn.simpleicons.org/x/000000' },
  linkedin: { color: '#0A66C2', icon: 'https://cdn.simpleicons.org/linkedin/0A66C2' },
  youtube: { color: '#FF0000', icon: 'https://cdn.simpleicons.org/youtube/FF0000' },
  tiktok: { color: '#000000', icon: 'https://cdn.simpleicons.org/tiktok/000000' },
  github: { color: '#181717', icon: 'https://cdn.simpleicons.org/github/181717' },
  whatsapp: { color: '#25D366', icon: 'https://cdn.simpleicons.org/whatsapp/25D366' },
  whatsapp_business: { color: '#25D366', icon: 'https://cdn.simpleicons.org/whatsapp/25D366' },
  telegram: { color: '#26A5E4', icon: 'https://cdn.simpleicons.org/telegram/26A5E4' },
  snapchat: { color: '#FFFC00', icon: 'https://cdn.simpleicons.org/snapchat/FFFC00' },
  discord: { color: '#5865F2', icon: 'https://cdn.simpleicons.org/discord/5865F2' },
  reddit: { color: '#FF4500', icon: 'https://cdn.simpleicons.org/reddit/FF4500' },
  threads: { color: '#000000', icon: 'https://cdn.simpleicons.org/threads/000000' },
};

const TEMPLATES = [
  { id: 'minimal', label: 'Minimal' },
  { id: 'modern', label: 'Modern' },
  { id: 'corporate', label: 'Corporate' },
];

const ACCENT_COLORS = [
  '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#000000',
];

const EmailSignature = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [template, setTemplate] = useState('minimal');
  const [accentColor, setAccentColor] = useState('#8B5CF6');
  const [showFields, setShowFields] = useState(true);

  const [fields, setFields] = useState({
    name: '', title: '', company: '', email: '', phone: '', website: '', address: '', photoUrl: '', profileLink: '',
  });

  const signatureRef = useRef(null);

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`);
      const p = res.data;
      setProfile(p);
      setFields({
        name: p.display_name || '',
        title: p.tagline || '',
        company: p.business_name || '',
        email: p.email || '',
        phone: p.phone || '',
        website: p.website || '',
        address: p.address || '',
        photoUrl: absoluteMediaUrl(resolveCanvasMediaUrl(p.avatar_url)),
        profileLink: `${BASE}/u/${p.username}`,
      });
      if (p.theme_color) setAccentColor(p.theme_color);
    } catch (e) {
      console.error('Failed to load profile:', e);
    } finally {
      setLoading(false);
    }
  };

  const socialLinks = (profile?.social_links || []).filter(l => l.is_active && SOCIAL_ICONS[l.platform]);

  const generateSignatureHTML = useCallback(() => {
    const f = fields;
    const socials = socialLinks.map(s => {
      const info = SOCIAL_ICONS[s.platform];
      const url = s.url.startsWith('http') ? s.url : `https://${s.url}`;
      return `<a href="${url}" target="_blank" style="text-decoration:none;margin-right:6px;display:inline-block;"><img src="${info.icon}" alt="${s.platform}" width="18" height="18" style="border-radius:3px;display:block;" /></a>`;
    }).join('');

    if (template === 'minimal') {
      return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#333333;">
  <tr><td style="padding-bottom:8px;">
    <span style="font-size:16px;font-weight:700;color:#111111;">${f.name}</span>
    ${f.title ? `<br/><span style="font-size:13px;color:#555555;">${f.title}</span>` : ''}
    ${f.company ? `<br/><span style="font-size:13px;color:#555555;">${f.company}</span>` : ''}
  </td></tr>
  <tr><td style="border-top:2px solid ${accentColor};padding-top:8px;font-size:12px;color:#666666;">
    ${f.phone ? `<span>&#128222; ${f.phone}</span><br/>` : ''}
    ${f.email ? `<span>&#9993; <a href="mailto:${f.email}" style="color:${accentColor};text-decoration:none;">${f.email}</a></span><br/>` : ''}
    ${f.website ? `<span>&#127760; <a href="${f.website.startsWith('http') ? f.website : 'https://' + f.website}" style="color:${accentColor};text-decoration:none;">${f.website}</a></span><br/>` : ''}
    ${f.address ? `<span>&#128205; ${f.address}</span><br/>` : ''}
  </td></tr>
  ${socials ? `<tr><td style="padding-top:8px;">${socials}</td></tr>` : ''}
  <tr><td style="padding-top:8px;"><a href="${f.profileLink}" style="font-size:11px;color:${accentColor};text-decoration:none;font-weight:600;">View my One Link profile &rarr;</a></td></tr>
</table>`;
    }

    if (template === 'modern') {
      return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#333333;">
  <tr>
    ${f.photoUrl ? `<td style="padding-right:14px;vertical-align:top;"><img src="${f.photoUrl}" alt="${f.name}" width="72" height="72" style="border-radius:50%;object-fit:cover;border:2px solid ${accentColor};" /></td>` : ''}
    <td style="vertical-align:top;">
      <span style="font-size:17px;font-weight:700;color:#111111;">${f.name}</span>
      ${f.title ? `<br/><span style="font-size:13px;color:${accentColor};font-weight:600;">${f.title}</span>` : ''}
      ${f.company ? `<br/><span style="font-size:12px;color:#777777;">${f.company}</span>` : ''}
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:8px;font-size:12px;color:#666666;">
        ${f.phone ? `<tr><td style="padding:1px 0;">&#128222; ${f.phone}</td></tr>` : ''}
        ${f.email ? `<tr><td style="padding:1px 0;">&#9993; <a href="mailto:${f.email}" style="color:${accentColor};text-decoration:none;">${f.email}</a></td></tr>` : ''}
        ${f.website ? `<tr><td style="padding:1px 0;">&#127760; <a href="${f.website.startsWith('http') ? f.website : 'https://' + f.website}" style="color:${accentColor};text-decoration:none;">${f.website}</a></td></tr>` : ''}
        ${f.address ? `<tr><td style="padding:1px 0;">&#128205; ${f.address}</td></tr>` : ''}
      </table>
      ${socials ? `<div style="margin-top:8px;">${socials}</div>` : ''}
      <div style="margin-top:8px;"><a href="${f.profileLink}" style="font-size:11px;color:${accentColor};text-decoration:none;font-weight:600;">View my One Link profile &rarr;</a></div>
    </td>
  </tr>
</table>`;
    }

    // corporate
    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#333333;border-left:4px solid ${accentColor};padding-left:14px;">
  <tr><td>
    <span style="font-size:18px;font-weight:700;color:#111111;">${f.name}</span>
    ${f.title ? `<span style="font-size:13px;color:#555555;margin-left:8px;">| ${f.title}</span>` : ''}
    ${f.company ? `<br/><span style="font-size:13px;font-weight:600;color:${accentColor};">${f.company}</span>` : ''}
  </td></tr>
  <tr><td style="padding-top:6px;"><hr style="border:none;border-top:1px solid #e0e0e0;margin:0;" /></td></tr>
  <tr><td style="padding-top:6px;font-size:12px;color:#666666;">
    <table cellpadding="0" cellspacing="0" border="0">
      <tr>
        ${f.phone ? `<td style="padding-right:16px;">&#128222; ${f.phone}</td>` : ''}
        ${f.email ? `<td style="padding-right:16px;">&#9993; <a href="mailto:${f.email}" style="color:${accentColor};text-decoration:none;">${f.email}</a></td>` : ''}
      </tr>
      <tr>
        ${f.website ? `<td style="padding-right:16px;padding-top:3px;">&#127760; <a href="${f.website.startsWith('http') ? f.website : 'https://' + f.website}" style="color:${accentColor};text-decoration:none;">${f.website}</a></td>` : ''}
        ${f.address ? `<td style="padding-top:3px;">&#128205; ${f.address}</td>` : ''}
      </tr>
    </table>
  </td></tr>
  ${socials ? `<tr><td style="padding-top:8px;">${socials}</td></tr>` : ''}
  <tr><td style="padding-top:8px;"><a href="${f.profileLink}" style="display:inline-block;padding:5px 14px;background:${accentColor};color:#ffffff;font-size:11px;font-weight:600;text-decoration:none;border-radius:4px;">View my One Link</a></td></tr>
</table>`;
  }, [fields, template, accentColor, socialLinks]);

  const handleCopyHTML = async () => {
    const html = generateSignatureHTML();
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([html], { type: 'text/plain' }),
        })
      ]);
      setCopied(true);
      toast.success('Signature copied! Paste into your email settings.');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: copy as plain HTML text
      try {
        await navigator.clipboard.writeText(html);
        setCopied(true);
        toast.success('HTML copied to clipboard!');
        setTimeout(() => setCopied(false), 2500);
      } catch {
        toast.error('Failed to copy. Try downloading instead.');
      }
    }
  };

  const handleDownloadPNG = async () => {
    const el = signatureRef.current;
    if (!el) return;
    try {
      const canvas = await html2canvas(el, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `email-signature-${fields.name.replace(/\s+/g, '-').toLowerCase() || 'onelink'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Signature downloaded as PNG!');
    } catch (e) {
      console.error('Download failed:', e);
      toast.error('Failed to download. Try copying instead.');
    }
  };

  const handleShareLink = async () => {
    const shareUrl = `${BASE}/signature/${profile?.username || ''}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success('Share link copied!');
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const updateField = (key, value) => setFields(prev => ({ ...prev, [key]: value }));

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="email-signature-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Email Signature</h1>
        <p className="text-sm text-[#71717A] mt-1">Create a professional email signature from your profile</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Controls */}
        <div className="space-y-5">
          {/* Template Picker */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 space-y-3">
            <h3 className="text-white font-semibold text-sm">Template</h3>
            <div className="grid grid-cols-3 gap-2">
              {TEMPLATES.map(t => (
                <button key={t.id} data-testid={`template-${t.id}`}
                  onClick={() => setTemplate(t.id)}
                  className={`px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${
                    template === t.id
                      ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/20'
                      : 'bg-[#1E1E1E] text-[#A1A1AA] hover:text-white border border-[#27272A] hover:border-[#3f3f46]'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Color Picker */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 space-y-3">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <Palette className="w-4 h-4" /> Accent Color
            </h3>
            <div className="flex flex-wrap gap-2">
              {ACCENT_COLORS.map(c => (
                <button key={c} data-testid={`color-${c.replace('#', '')}`}
                  onClick={() => setAccentColor(c)}
                  className={`w-8 h-8 rounded-full transition-all ${
                    accentColor === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#121212] scale-110' : 'hover:scale-110'
                  }`}
                  style={{ backgroundColor: c }} />
              ))}
              <div className="flex items-center gap-1.5 ml-1">
                <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                  className="w-8 h-8 rounded-full cursor-pointer bg-transparent border-0 p-0" />
                <span className="text-xs text-[#71717A]">Custom</span>
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 space-y-3">
            <button onClick={() => setShowFields(!showFields)}
              className="flex items-center justify-between w-full text-white font-semibold text-sm">
              <span>Edit Info</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFields ? 'rotate-180' : ''}`} />
            </button>
            {showFields && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                className="space-y-3 pt-2">
                {[
                  { key: 'name', label: 'Full Name', icon: User, placeholder: 'John Doe' },
                  { key: 'title', label: 'Job Title / Tagline', icon: Briefcase, placeholder: 'CEO & Founder' },
                  { key: 'company', label: 'Company', icon: Building2, placeholder: 'Acme Inc' },
                  { key: 'email', label: 'Email', icon: Mail, placeholder: 'you@company.com' },
                  { key: 'phone', label: 'Phone', icon: Phone, placeholder: '+1 555 0123' },
                  { key: 'website', label: 'Website', icon: Globe, placeholder: 'www.company.com' },
                  { key: 'address', label: 'Address', icon: MapPin, placeholder: '123 Main St' },
                ].map(f => (
                  <div key={f.key}>
                    <Label className="text-[#A1A1AA] text-xs flex items-center gap-1.5 mb-1">
                      <f.icon className="w-3 h-3" /> {f.label}
                    </Label>
                    <Input data-testid={`sig-field-${f.key}`}
                      value={fields[f.key]}
                      onChange={e => updateField(f.key, e.target.value)}
                      className="bg-[#1E1E1E] border-[#27272A] text-white h-9 text-sm"
                      placeholder={f.placeholder} />
                  </div>
                ))}
              </motion.div>
            )}
          </div>
        </div>

        {/* Right: Preview + Actions */}
        <div className="space-y-4">
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 space-y-4">
            <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
              <Eye className="w-4 h-4" /> Live Preview
            </div>
            {/* Signature Preview (white bg like in email) */}
            <div className="bg-white rounded-xl p-5 min-h-[120px]" data-testid="signature-preview">
              <div ref={signatureRef} dangerouslySetInnerHTML={{ __html: generateSignatureHTML() }} />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button data-testid="copy-signature" onClick={handleCopyHTML}
              className={`flex-1 rounded-xl h-11 font-medium transition-all ${
                copied ? 'bg-green-600 hover:bg-green-700' : 'bg-violet-600 hover:bg-violet-700'
              }`}>
              {copied ? <><Check className="w-4 h-4 mr-2" /> Copied!</> : <><Copy className="w-4 h-4 mr-2" /> Copy HTML</>}
            </Button>
            <Button data-testid="download-signature" variant="outline" onClick={handleDownloadPNG}
              className="flex-1 border-[#27272A] text-[#A1A1AA] hover:text-white rounded-xl h-11 font-medium">
              <Download className="w-4 h-4 mr-2" /> PNG
            </Button>
            <Button data-testid="share-signature" variant="outline" onClick={handleShareLink}
              className={`flex-1 rounded-xl h-11 font-medium transition-all ${
                linkCopied ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : 'border-[#27272A] text-[#A1A1AA] hover:text-white'
              }`}>
              {linkCopied ? <><Check className="w-4 h-4 mr-2" /> Copied!</> : <><Share2 className="w-4 h-4 mr-2" /> Share</>}
            </Button>
          </div>

          {/* Instructions */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 space-y-3">
            <h3 className="text-white font-semibold text-sm">How to use your signature</h3>
            <div className="space-y-2 text-xs text-[#A1A1AA]">
              <div className="flex gap-2">
                <span className="w-5 h-5 rounded-full bg-violet-600/20 text-violet-400 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                <p>Click <strong className="text-white">Copy to Clipboard</strong></p>
              </div>
              <div className="flex gap-2">
                <span className="w-5 h-5 rounded-full bg-violet-600/20 text-violet-400 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                <p>Open your email app settings (Gmail, Outlook, etc.)</p>
              </div>
              <div className="flex gap-2">
                <span className="w-5 h-5 rounded-full bg-violet-600/20 text-violet-400 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                <p>Find <strong className="text-white">Signature settings</strong> and paste</p>
              </div>
              <div className="mt-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                <p className="text-amber-400/80 text-xs"><strong>Gmail:</strong> Settings → See all settings → General → Signature</p>
                <p className="text-amber-400/80 text-xs mt-1"><strong>Outlook:</strong> Settings → Mail → Compose and reply → Email signature</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSignature;
